// src/pages/ConnectionTest.js
import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, getDocs, query, limit, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadString, getDownloadURL } from 'firebase/storage';

const ConnectionTest = () => {
  const [tests, setTests] = useState({
    auth: { status: 'pending', message: 'Not started' },
    firestore: { status: 'pending', message: 'Not started' },
    storage: { status: 'pending', message: 'Not started' }
  });
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState([]);

  const addLog = (message) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
    console.log(message);
  };

  // Check authentication on component mount
  useEffect(() => {
    testAuth();
  }, []);

  const testAuth = () => {
    setTests(prev => ({
      ...prev,
      auth: { status: 'running', message: 'Testing authentication...' }
    }));
    addLog('Testing Firebase Authentication connection...');

    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, 
      (user) => {
        if (user) {
          setUser(user);
          setTests(prev => ({
            ...prev,
            auth: { 
              status: 'success', 
              message: `Connected and authenticated as ${user.email}` 
            }
          }));
          addLog(`✅ Authentication successful as ${user.email}`);
        } else {
          setUser(null);
          setTests(prev => ({
            ...prev,
            auth: { 
              status: 'warning', 
              message: 'Connected but not authenticated' 
            }
          }));
          addLog('⚠️ Firebase Auth connected but no user is logged in');
        }
      },
      (error) => {
        setTests(prev => ({
          ...prev,
          auth: { 
            status: 'error', 
            message: `Error: ${error.message}` 
          }
        }));
        addLog(`❌ Authentication error: ${error.message}`);
      }
    );

    return () => unsubscribe();
  };

  const testFirestore = async () => {
    setTests(prev => ({
      ...prev,
      firestore: { status: 'running', message: 'Testing Firestore...' }
    }));
    addLog('Testing Firebase Firestore connection...');

    try {
      // First try to read from a test collection
      const db = getFirestore();
      const testQuery = query(collection(db, 'connectionTests'), limit(1));
      
      addLog('Attempting to read from Firestore...');
      await getDocs(testQuery);
      addLog('✅ Read from Firestore successful');
      
      // Then try to write to the test collection
      addLog('Attempting to write to Firestore...');
      const docRef = await addDoc(collection(db, 'connectionTests'), {
        test: 'Connection test',
        timestamp: serverTimestamp(),
        userEmail: user?.email || 'anonymous'
      });
      
      addLog(`✅ Write to Firestore successful, document ID: ${docRef.id}`);
      setTests(prev => ({
        ...prev,
        firestore: { 
          status: 'success', 
          message: 'Successfully connected to Firestore' 
        }
      }));
    } catch (error) {
      addLog(`❌ Firestore error: ${error.message}`);
      setTests(prev => ({
        ...prev,
        firestore: { 
          status: 'error', 
          message: `Error: ${error.message}` 
        }
      }));
    }
  };

  const testStorage = async () => {
    setTests(prev => ({
      ...prev,
      storage: { status: 'running', message: 'Testing Storage...' }
    }));
    addLog('Testing Firebase Storage connection...');

    try {
      const storage = getStorage();
      const testRef = ref(storage, `connectionTests/${Date.now()}.txt`);
      
      addLog('Attempting to upload to Storage...');
      await uploadString(testRef, 'This is a connection test');
      
      addLog('Attempting to get download URL...');
      const url = await getDownloadURL(testRef);
      
      addLog(`✅ Storage test successful, URL: ${url}`);
      setTests(prev => ({
        ...prev,
        storage: { 
          status: 'success', 
          message: 'Successfully connected to Storage' 
        }
      }));
    } catch (error) {
      addLog(`❌ Storage error: ${error.message}`);
      setTests(prev => ({
        ...prev,
        storage: { 
          status: 'error', 
          message: `Error: ${error.message}` 
        }
      }));
    }
  };

  const testAll = () => {
    testAuth();
    testFirestore();
    testStorage();
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return '#4caf50';
      case 'error': return '#f44336';
      case 'warning': return '#ff9800';
      case 'running': return '#2196f3';
      default: return '#9e9e9e';
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ marginBottom: '20px' }}>Firebase Connection Test</h1>
      <p style={{ marginBottom: '20px' }}>
        This utility tests connectivity to Firebase services to help identify configuration issues.
      </p>
      
      <div style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
        <button 
          onClick={testAll}
          style={{
            backgroundColor: '#673ab7',
            color: 'white',
            border: 'none',
            padding: '10px 15px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Test All Services
        </button>
        
        <button 
          onClick={testAuth}
          style={{
            backgroundColor: '#2196f3',
            color: 'white',
            border: 'none',
            padding: '10px 15px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Test Auth
        </button>
        
        <button 
          onClick={testFirestore}
          style={{
            backgroundColor: '#ff9800',
            color: 'white',
            border: 'none',
            padding: '10px 15px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Test Firestore
        </button>
        
        <button 
          onClick={testStorage}
          style={{
            backgroundColor: '#4caf50',
            color: 'white',
            border: 'none',
            padding: '10px 15px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Test Storage
        </button>
      </div>
      
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>Test Results</h2>
        
        {Object.entries(tests).map(([service, { status, message }]) => (
          <div 
            key={service}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '15px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              marginBottom: '10px'
            }}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: getStatusColor(status),
                marginRight: '15px',
                flexShrink: 0
              }}
            ></div>
            
            <div style={{ flex: 1 }}>
              <strong style={{ textTransform: 'capitalize' }}>{service}</strong>
              <div>{message}</div>
            </div>
            
            {status === 'running' && (
              <div 
                style={{ 
                  width: '20px', 
                  height: '20px', 
                  borderRadius: '50%',
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid #2196f3',
                  animation: 'spin 1s linear infinite'
                }}
              ></div>
            )}
          </div>
        ))}
      </div>
      
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '4px'
      }}>
        <h3 style={{ marginTop: '0', marginBottom: '10px' }}>Connection Logs</h3>
        <div style={{
          backgroundColor: '#000',
          color: '#0f0',
          fontFamily: 'monospace',
          padding: '10px',
          borderRadius: '4px',
          maxHeight: '400px',
          overflow: 'auto'
        }}>
          {logs.length === 0 ? (
            <p>No logs yet. Run a test to see logs.</p>
          ) : (
            logs.map((log, index) => (
              <div key={index}>{log}</div>
            ))
          )}
        </div>
      </div>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ConnectionTest;